package fr.frux.feature.feedback

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.asAppError
import fr.frux.core.common.error.isExpectedFailure
import fr.frux.core.common.logging.Logger
import fr.frux.shared.content.FeedbackLimits
import fr.frux.shared.domain.model.FeedbackCategory
import fr.frux.shared.domain.model.FeedbackDraft
import fr.frux.shared.domain.repository.FeedbackRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/** Identifiant stable de l'écran d'origine « Profil », seule entrée à ce jour (ADR 0028). */
const val FEEDBACK_SOURCE_PROFILE = "profile"

/**
 * État du formulaire « Donner mon avis » (#437). [sent] et [error] sont des one-shots portés par
 * l'état et non par un effet d'écran : Nav3 détruit les effets locaux (voir `SnackbarMessageEffect`).
 */
data class FeedbackState(
    val category: FeedbackCategory = FeedbackCategory.OTHER,
    val message: String = "",
    val isSending: Boolean = false,
    val error: AppError? = null,
    val sent: Boolean = false,
) {
    /** Borne sur le texte trimé, comptée en points de code comme le serveur et `char_length`. */
    val canSend: Boolean
        get() =
            !isSending && !sent &&
                message.trim().codePointLength() in FeedbackLimits.MESSAGE_MIN..FeedbackLimits.MESSAGE_MAX
}

/** Points de code sans API JVM : une paire de substitution ne compte qu'une fois (emojis). */
internal fun String.codePointLength(): Int = count { !it.isLowSurrogate() }

/**
 * Formulaire de retour utilisateur (#437, ADR 0028). Le ViewModel ne fournit que la catégorie, le
 * texte et l'écran d'origine : la version de l'app et la plateforme sont jointes par le repository.
 */
class FeedbackViewModel(
    private val sourceScreen: String,
    private val repository: FeedbackRepository,
) : ViewModel() {
    private val _state = MutableStateFlow(FeedbackState())
    val state: StateFlow<FeedbackState> = _state.asStateFlow()

    fun onCategorySelected(category: FeedbackCategory) = _state.update { it.copy(category = category) }

    fun onMessageChange(text: String) =
        _state.update {
            it.copy(message = text.take(FeedbackLimits.MESSAGE_MAX).withoutDanglingSurrogate())
        }

    fun consumeError() = _state.update { it.copy(error = null) }

    fun send() {
        val current = _state.value
        if (!current.canSend) return
        // Synchrone, avant tout `launch` : garde anti double appui (même patron que CreateHarvestViewModel).
        _state.update { it.copy(isSending = true, error = null) }
        viewModelScope.launch {
            repository
                .send(FeedbackDraft(current.category, current.message.trim(), sourceScreen))
                .fold(
                    onSuccess = { _state.update { it.copy(isSending = false, sent = true) } },
                    onFailure = { failure ->
                        // Échec attendu (limite, réseau, 400) : affiché, jamais en Logger.e, dont le
                        // pont vers Sentry ferait un event de chaque refus métier (#310).
                        if (failure.isExpectedFailure) {
                            Logger.i(TAG, "Avis refusé (${failure::class.simpleName})")
                        } else {
                            Logger.e(TAG, "Envoi d'avis en échec", failure)
                        }
                        _state.update { it.copy(isSending = false, error = failure.asAppError()) }
                    },
                )
        }
    }

    private companion object {
        const val TAG = "FeedbackViewModel"
    }
}

/**
 * #437 : `take(MESSAGE_MAX)` peut couper au milieu d'une paire de substitution (emoji hors
 * BMP). Un high surrogate en dernière position n'a plus son low surrogate : on le retire plutôt
 * que de laisser un demi-caractère invalide dans l'état.
 */
private fun String.withoutDanglingSurrogate(): String =
    if (isNotEmpty() && last().isHighSurrogate()) dropLast(1) else this
